/*
  rs-preview-capture.js
  Shared add-to-cart preview capture for the rs- custom sign configurators
  (street, square, diamond, octagon sections).

  At add-to-cart it rasterizes the live preview to a PNG and attaches it to the
  product form as a hidden file line-item property `_Preview image`. Dawn's
  product-form.js submits the form as multipart FormData, so the PNG uploads to
  Shopify's CDN like any customer file upload. The cart templates then use the
  resulting /uploads/ URL as the line-item thumbnail; the underscore prefix
  keeps the property itself out of every customer-facing property list.

  Registration (from each section's inline script, before or after this file
  loads — the queue pattern makes order irrelevant):

    (window.RSPreviewQueue = window.RSPreviewQueue || []).push({
      form: '#rs-csq-form',            // selector of the cart form
      button: '#rs-csq-add',           // selector of the submit button
      getSVG: function () { ... },     // SVG mode: return the live <svg>
      // OR
      draw: function (canvas) { ... }, // canvas mode: size + draw the canvas
      fonts: { 'FHWA Series B': 'https://cdn.shopify.com/...woff2' },
      // SVG mode: families to embed as base64 @font-face when present.
      fontsReady: ['FHWA Series B'],   // canvas mode: await document.fonts
      targetWidth: 1000                // capture width in px (capped at 1200)
    });

  SVG mode never rasterizes <text> inside the SVG image: WebKit paints an
  SVG-as-image before @font-face fonts inside it finish loading, and text in
  a still-loading font is painted invisible — the one-shot canvas capture
  bakes that in (Safari desktop + iOS; the source of textless order
  previews). Instead, <text> nodes are stripped from the clone and redrawn
  onto the canvas with fillText using the page's already-loaded fonts. This
  assumes what the configurators guarantee: every <text> is a direct child
  of the svg root (no transformed <g> ancestors), positioned by x/y with
  text-anchor / dominant-baseline attributes.

  Pictures inside the drawing (symbols, arrows, customer uploads) have the
  same kind of race (2026-10-09): WebKit fires the SVG image's load event
  before the <image> elements inside it are decoded, so a draw right after
  load left every symbol out of Safari cart pictures. SVG pictures are
  swapped for a nested <svg> (ids, classes and <style> rules scoped so they
  cannot touch the sign), which paints with the rest of the drawing. Any
  <image> left (PNG / JPEG uploads) is redrawn until the canvas differs from
  a render without pictures, up to PICTURE_WAIT_MS.

  Failure policy: capture races a timeout and every error is swallowed — the
  form always submits, at worst without a preview. Add to cart is never blocked.

  "Preview full size" button (2026-10-06): every registration also gets a
  button under the sign's size label (.rs-size-label in the same section).
  It opens a new tab with a clean picture of the current design in the store
  listing-photo format: the sign trimmed to its own edges, longest side
  1400px, centered on a 1500x1500 white square, JPEG q70 stepped down until
  under 110 KB (same spec as the Wikimedia listing images). No watermark, no
  page chrome. The tab offers a Download link named after the product handle.
  Canvas-mode draw() receives the wanted width as a second argument; a
  section that ignores it is scaled up to fit.
*/

(function () {
  'use strict';

  var CAPTURE_TIMEOUT_MS = 3000;
  var PICTURE_WAIT_MS = 1500;      /* Safari paints <image> pictures ~50 ms after load */
  var PICTURE_POLL_MS = 50;
  var SVG_NS = 'http://www.w3.org/2000/svg';
  var pictureCount = 0;            /* unique prefix per inlined picture */
  var MAX_TARGET_WIDTH = 1200;
  var PROPERTY_NAME = 'properties[_Preview image]';
  var fontDataCache = {}; /* woff2 url -> Promise<base64 string> */

  var LISTING_CANVAS = 1500;       /* final square, px */
  var LISTING_SIGN = 1400;         /* sign's longest side inside it */
  var LISTING_RENDER = 2000;       /* render bigger, then scale down: crisp edges */
  var LISTING_MAX_BYTES = 110000;
  var LISTING_QUALITY = 0.7;
  var LISTING_TIMEOUT_MS = 15000;

  /* ------------------------------------------------------------------ */
  /* Helpers                                                             */
  /* ------------------------------------------------------------------ */

  function timeout(ms) {
    return new Promise(function (resolve, reject) {
      setTimeout(function () {
        reject(new Error('rs-preview-capture: timed out'));
      }, ms);
    });
  }

  function bufferToBase64(buffer) {
    var bytes = new Uint8Array(buffer);
    var chunk = 0x8000;
    var parts = [];
    for (var i = 0; i < bytes.length; i += chunk) {
      parts.push(String.fromCharCode.apply(null, bytes.subarray(i, i + chunk)));
    }
    return btoa(parts.join(''));
  }

  function fetchFontBase64(url) {
    if (!fontDataCache[url]) {
      fontDataCache[url] = fetch(url)
        .then(function (response) {
          if (!response.ok) throw new Error('rs-preview-capture: font fetch failed');
          return response.arrayBuffer();
        })
        .then(bufferToBase64)
        .catch(function (error) {
          delete fontDataCache[url]; /* allow a retry on the next add */
          throw error;
        });
    }
    return fontDataCache[url];
  }

  function blobToDataURL(blob) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function () { resolve(reader.result); };
      reader.onerror = function () { reject(new Error('rs-preview-capture: file read failed')); };
      reader.readAsDataURL(blob);
    });
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('rs-preview-capture: image decode failed')); };
      img.src = url;
    });
  }

  function canvasToPngBlob(canvas) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob);
        else reject(new Error('rs-preview-capture: toBlob returned null'));
      }, 'image/png');
    });
  }

  /* ------------------------------------------------------------------ */
  /* SVG mode: clone the live SVG, inline fonts + blob images, rasterize */
  /* ------------------------------------------------------------------ */

  function inlineBlobImages(clone) {
    var images = clone.querySelectorAll('image');
    var jobs = [];

    Array.prototype.forEach.call(images, function (imageEl) {
      var href = imageEl.getAttribute('href') || imageEl.getAttribute('xlink:href') || '';
      if (href.indexOf('blob:') !== 0) return;

      jobs.push(
        fetch(href)
          .then(function (response) { return response.blob(); })
          .then(blobToDataURL)
          .then(function (dataUrl) {
            imageEl.setAttribute('href', dataUrl);
            imageEl.removeAttribute('xlink:href');
          })
      );
    });

    return Promise.all(jobs);
  }

  /* ------------------------------------------------------------------ */
  /* Pictures inside the drawing: inline SVG ones, wait for the rest     */
  /* ------------------------------------------------------------------ */

  function decodeSvgDataUrl(href) {
    var comma = href.indexOf(',');
    if (comma < 0) return '';
    var body = href.slice(comma + 1);
    if (!/;base64$/i.test(href.slice(0, comma))) return decodeURIComponent(body);
    var binary = atob(body);
    /* FileReader uploads are UTF-8 bytes; the symbols are btoa() Latin-1 */
    try { return decodeURIComponent(escape(binary)); } catch (error) { return binary; }
  }

  /* Rules from a picture's <style> must only reach that picture: every
     selector gets the picture's own id in front, id selectors follow the
     renamed ids. Only the text right before each "{" is a selector. */
  function scopeCss(css, scope, renamed) {
    return css.replace(/\/\*[\s\S]*?\*\//g, '').replace(/(^|[{}])([^{}@]+)(?=\{)/g, function (m, brace, selectors) {
      return brace + selectors.split(',').map(function (selector) {
        var s = selector.trim();
        if (!s || /^(from|to|\d)/.test(s)) return selector; /* keyframe steps */
        s = s.replace(/#(-?[_a-zA-Z][\w-]*)/g, function (h, id) {
          return renamed[id] ? '#' + renamed[id] : h;
        });
        return /^(:root|svg)\b/.test(s)
          ? '#' + scope + s.replace(/^(:root|svg)/, '')
          : '#' + scope + ' ' + s;
      }).join(',');
    });
  }

  /* Give the picture's ids a unique prefix (and update every #id / url(#id)
     reference) so two pictures, or a picture and the sign, never clash. */
  function scopePicture(root, scope) {
    var renamed = {};
    if (root.getAttribute('id')) renamed[root.getAttribute('id')] = scope;
    Array.prototype.forEach.call(root.querySelectorAll('[id]'), function (el) {
      var id = el.getAttribute('id');
      renamed[id] = scope + '-' + id;
      el.setAttribute('id', renamed[id]);
    });
    root.setAttribute('id', scope);

    function fixRefs(text) {
      return text.replace(/url\(\s*(['"]?)#([^'")\s]+)\1\s*\)/g, function (m, quote, id) {
        return renamed[id] ? 'url(#' + renamed[id] + ')' : m;
      });
    }

    var nodes = [root].concat(Array.prototype.slice.call(root.querySelectorAll('*')));
    nodes.forEach(function (el) {
      Array.prototype.slice.call(el.attributes).forEach(function (attr) {
        var value = attr.value;
        if (attr.localName === 'href' && value.charAt(0) === '#' && renamed[value.slice(1)]) {
          el.setAttributeNS(attr.namespaceURI, attr.name, '#' + renamed[value.slice(1)]);
        } else if (value.indexOf('url(') !== -1) {
          el.setAttributeNS(attr.namespaceURI, attr.name, fixRefs(value));
        }
      });
      if (el.localName === 'style') el.textContent = scopeCss(fixRefs(el.textContent), scope, renamed);
    });
  }

  /* Attributes of the <image> that still apply around the nested <svg> */
  var PICTURE_ATTRS = ['transform', 'clip-path', 'mask', 'filter', 'opacity', 'style', 'display', 'visibility'];

  /* The <image> as a nested <svg> placed on the same box, or null when the
     picture cannot be read (it then stays an <image>). */
  function svgPicture(imageEl, href) {
    var root;
    try {
      var doc = new DOMParser().parseFromString(decodeSvgDataUrl(href), 'image/svg+xml');
      root = doc.documentElement;
      if (!root || root.namespaceURI !== SVG_NS || root.localName !== 'svg' ||
          doc.getElementsByTagName('parsererror').length) return null;
    } catch (error) {
      return null;
    }

    if (!root.getAttribute('viewBox')) {
      var w = root.getAttribute('width') || '';
      var h = root.getAttribute('height') || '';
      if (!(parseFloat(w) > 0 && parseFloat(h) > 0) || /%/.test(w + h)) return null;
      root.setAttribute('viewBox', '0 0 ' + parseFloat(w) + ' ' + parseFloat(h));
    }

    var node = document.importNode(root, true);
    scopePicture(node, 'rs-pic' + (++pictureCount));
    ['x', 'y', 'width', 'height'].forEach(function (name) {
      node.setAttribute(name, imageEl.getAttribute(name) || '0');
    });
    node.setAttribute('preserveAspectRatio', imageEl.getAttribute('preserveAspectRatio') || 'xMidYMid meet');
    node.setAttribute('overflow', 'hidden');

    var wrapper = null;
    PICTURE_ATTRS.forEach(function (name) {
      if (!imageEl.hasAttribute(name)) return;
      wrapper = wrapper || document.createElementNS(SVG_NS, 'g');
      wrapper.setAttribute(name, imageEl.getAttribute(name));
    });
    if (wrapper) wrapper.appendChild(node);
    return wrapper || node;
  }

  function inlineSvgPictures(clone) {
    Array.prototype.forEach.call(clone.querySelectorAll('image'), function (imageEl) {
      var href = imageEl.getAttribute('href') || imageEl.getAttribute('xlink:href') || '';
      if (!/^data:image\/svg\+xml[;,]/i.test(href)) return;
      if (!imageEl.getAttribute('width') || !imageEl.getAttribute('height')) return;
      var picture = svgPicture(imageEl, href);
      if (picture) imageEl.parentNode.replaceChild(picture, imageEl);
    });
  }

  /* Canvas boxes of the <image> pictures still in the clone. Only data:
     pictures ever load inside an svg image; a picture that is not a plain
     root child gets the whole canvas as its box. */
  function pictureRegions(clone, minX, minY, scale, w, h) {
    var regions = [];
    Array.prototype.forEach.call(clone.querySelectorAll('image'), function (el) {
      var href = el.getAttribute('href') || el.getAttribute('xlink:href') || '';
      if (href.indexOf('data:') !== 0) return;
      var box = { x0: 0, y0: 0, x1: w, y1: h };
      if (el.parentNode === clone && !el.hasAttribute('transform')) {
        var x = parseFloat(el.getAttribute('x')) || 0;
        var y = parseFloat(el.getAttribute('y')) || 0;
        box = {
          x0: Math.max(0, Math.floor((x - minX) * scale)),
          y0: Math.max(0, Math.floor((y - minY) * scale)),
          x1: Math.min(w, Math.ceil((x + (parseFloat(el.getAttribute('width')) || 0) - minX) * scale)),
          y1: Math.min(h, Math.ceil((y + (parseFloat(el.getAttribute('height')) || 0) - minY) * scale))
        };
      }
      if (box.x1 > box.x0 && box.y1 > box.y0) regions.push(box);
    });
    return regions;
  }

  function markupWithoutPictures(clone) {
    var copy = clone.cloneNode(true);
    Array.prototype.forEach.call(copy.querySelectorAll('image'), function (el) {
      el.parentNode.removeChild(el);
    });
    return new XMLSerializer().serializeToString(copy);
  }

  /* Redraw img until every picture box differs from a render without the
     pictures (WebKit paints them a beat after load), or PICTURE_WAIT_MS. */
  function waitForPictures(ctx, img, bareMarkup, regions, w, h) {
    var ux0 = w, uy0 = h, ux1 = 0, uy1 = 0;
    regions.forEach(function (r) {
      ux0 = Math.min(ux0, r.x0); uy0 = Math.min(uy0, r.y0);
      ux1 = Math.max(ux1, r.x1); uy1 = Math.max(uy1, r.y1);
    });
    var uw = ux1 - ux0;
    var uh = uy1 - uy0;

    function pixels(context) {
      return new Uint32Array(context.getImageData(ux0, uy0, uw, uh).data.buffer);
    }

    function painted(now, base) {
      return regions.every(function (r) {
        for (var y = r.y0; y < r.y1; y++) {
          var row = (y - uy0) * uw - ux0;
          for (var x = r.x0; x < r.x1; x++) {
            if (now[row + x] !== base[row + x]) return true;
          }
        }
        return false;
      });
    }

    var bareUrl = URL.createObjectURL(new Blob([bareMarkup], { type: 'image/svg+xml;charset=utf-8' }));
    return loadImage(bareUrl).then(function (bare) {
      URL.revokeObjectURL(bareUrl);
      var probe = document.createElement('canvas');
      probe.width = w;
      probe.height = h;
      var probeCtx = probe.getContext('2d');
      probeCtx.drawImage(bare, 0, 0, w, h);
      var base = pixels(probeCtx);
      var started = Date.now();

      return new Promise(function (resolve) {
        (function check() {
          if (painted(pixels(ctx), base) || Date.now() - started >= PICTURE_WAIT_MS) {
            resolve();
            return;
          }
          setTimeout(function () {
            ctx.clearRect(0, 0, w, h);
            ctx.drawImage(img, 0, 0, w, h);
            check();
          }, PICTURE_POLL_MS);
        })();
      });
    }, function () {
      URL.revokeObjectURL(bareUrl);
    }).catch(function () { /* no baseline or pixels: keep what was drawn */ });
  }

  function buildFontStyle(markup, fonts) {
    var families = Object.keys(fonts || {}).filter(function (family) {
      return markup.indexOf(family) !== -1;
    });
    if (!families.length) return Promise.resolve('');

    return Promise.all(
      families.map(function (family) {
        return fetchFontBase64(fonts[family]).then(function (base64) {
          return (
            "@font-face{font-family:'" + family + "';font-style:normal;font-weight:400;" +
            "src:url(data:font/woff2;base64," + base64 + ") format('woff2');}"
          );
        });
      })
    ).then(function (rules) {
      return rules.join('');
    });
  }

  /* CSS font shorthand wants quotes around family names with spaces; the
     svg attribute carries them bare ("FHWA Series C, Arial, sans-serif"). */
  function canvasFontFamily(familyAttr) {
    var names = String(familyAttr || 'Arial, sans-serif').split(',');
    var out = [];
    for (var i = 0; i < names.length; i++) {
      var name = names[i].trim().replace(/^['"]+|['"]+$/g, '');
      if (!name) continue;
      out.push(/[^a-zA-Z-]/.test(name) ? '"' + name + '"' : name);
    }
    return out.join(', ');
  }

  /* Pull every <text> out of the clone so the SVG image holds shapes only,
     returning what fillText needs to redraw them on the canvas. */
  function extractTexts(clone) {
    var nodes = clone.querySelectorAll('text');
    var texts = [];

    Array.prototype.forEach.call(nodes, function (node) {
      var baseline = node.getAttribute('dominant-baseline');
      texts.push({
        content: node.textContent,
        x: parseFloat(node.getAttribute('x')) || 0,
        y: parseFloat(node.getAttribute('y')) || 0,
        size: parseFloat(node.getAttribute('font-size')) || 16,
        weight: node.getAttribute('font-weight') || '400',
        family: canvasFontFamily(node.getAttribute('font-family')),
        fill: node.getAttribute('fill') || '#000000',
        anchor: node.getAttribute('text-anchor') || 'start',
        central: baseline === 'central' || baseline === 'middle',
        maxw: parseFloat(node.getAttribute('data-maxw')) || 0
      });
      node.parentNode.removeChild(node);
    });

    return texts;
  }

  function pageFontsReady(texts) {
    if (!texts.length || !document.fonts || !document.fonts.load) {
      return Promise.resolve();
    }
    var seen = {};
    var jobs = [];
    texts.forEach(function (t) {
      var spec = t.weight + ' ' + Math.ceil(t.size) + 'px ' + t.family;
      if (seen[spec]) return;
      seen[spec] = true;
      jobs.push(document.fonts.load(spec).catch(function () { /* fall back */ }));
    });
    return Promise.all(jobs).catch(function () { /* draw with fallbacks */ });
  }

  function drawTexts(ctx, texts, minX, minY, scale) {
    texts.forEach(function (t) {
      if (!t.content) return;
      ctx.font = t.weight + ' ' + (t.size * scale) + 'px ' + t.family;
      ctx.fillStyle = t.fill;
      ctx.textAlign = t.anchor === 'middle' ? 'center' : (t.anchor === 'end' ? 'right' : 'left');
      ctx.textBaseline = t.central ? 'middle' : 'alphabetic';

      var x = (t.x - minX) * scale;
      var y = (t.y - minY) * scale;
      var limit = t.maxw > 0 ? t.maxw * scale : 0;
      var width = limit ? ctx.measureText(t.content).width : 0;

      if (limit && width > limit) {
        /* mirror the configurators' data-maxw clamp: squeeze, don't clip */
        ctx.save();
        ctx.translate(x, 0);
        ctx.scale(limit / width, 1);
        ctx.fillText(t.content, 0, y);
        ctx.restore();
      } else {
        ctx.fillText(t.content, x, y);
      }
    });
  }

  function captureSvg(registration) {
    return renderSvg(registration, function () {
      return Math.min(registration.targetWidth || 1000, MAX_TARGET_WIDTH);
    }).then(canvasToPngBlob);
  }

  /* Rasterize the live svg onto a canvas. pickWidth(sourceW, sourceH)
     returns the canvas width in px; height follows the svg's ratio. */
  function renderSvg(registration, pickWidth) {
    var svg = typeof registration.getSVG === 'function' ? registration.getSVG() : null;
    if (!svg) return Promise.reject(new Error('rs-preview-capture: no svg'));

    var viewBox = svg.viewBox && svg.viewBox.baseVal;
    var sourceW = (viewBox && viewBox.width) || parseFloat(svg.getAttribute('width')) || svg.clientWidth;
    var sourceH = (viewBox && viewBox.height) || parseFloat(svg.getAttribute('height')) || svg.clientHeight;
    if (!sourceW || !sourceH) return Promise.reject(new Error('rs-preview-capture: svg has no size'));

    var minX = viewBox ? viewBox.x : 0;
    var minY = viewBox ? viewBox.y : 0;

    var targetW = Math.round(pickWidth(sourceW, sourceH));
    var targetH = Math.round(targetW * (sourceH / sourceW));
    var scale = targetW / sourceW;

    var clone = svg.cloneNode(true);
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('xmlns:xlink', 'http://www.w3.org/1999/xlink');
    if (!clone.getAttribute('viewBox')) {
      clone.setAttribute('viewBox', '0 0 ' + sourceW + ' ' + sourceH);
    }
    /* The live svg carries on-screen pixel width/height; without overriding
       them Safari and Firefox rasterize at on-screen size. */
    clone.setAttribute('width', targetW);
    clone.setAttribute('height', targetH);

    var texts = extractTexts(clone);
    var fontsReady = pageFontsReady(texts);

    return inlineBlobImages(clone)
      .then(function () {
        inlineSvgPictures(clone);
        var markup = new XMLSerializer().serializeToString(clone);
        /* With <text> stripped no family names remain in the markup, so this
           resolves '' without fetching — kept for any future non-text use. */
        return buildFontStyle(markup, registration.fonts).then(function (css) {
          if (!css) return markup;
          var styleEl = document.createElementNS('http://www.w3.org/2000/svg', 'style');
          styleEl.textContent = css;
          clone.insertBefore(styleEl, clone.firstChild);
          return new XMLSerializer().serializeToString(clone);
        });
      })
      .then(function (markup) {
        var regions = pictureRegions(clone, minX, minY, scale, targetW, targetH);
        var bareMarkup = regions.length ? markupWithoutPictures(clone) : '';
        var svgBlob = new Blob([markup], { type: 'image/svg+xml;charset=utf-8' });
        var url = URL.createObjectURL(svgBlob);
        return loadImage(url).then(
          function (img) {
            var canvas = document.createElement('canvas');
            canvas.width = targetW;
            canvas.height = targetH;
            var ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, targetW, targetH);
            var pictures = regions.length
              ? waitForPictures(ctx, img, bareMarkup, regions, targetW, targetH)
              : Promise.resolve();
            return pictures.then(function () {
              URL.revokeObjectURL(url);
              return fontsReady;
            }).then(function () {
              drawTexts(ctx, texts, minX, minY, scale);
              return canvas;
            });
          },
          function (error) {
            URL.revokeObjectURL(url);
            throw error;
          }
        );
      });
  }

  /* ------------------------------------------------------------------ */
  /* Canvas mode: the section draws its own preview (street sign)        */
  /* ------------------------------------------------------------------ */

  function captureCanvas(registration) {
    return drawCanvas(registration).then(canvasToPngBlob);
  }

  /* width is passed on to draw(); sections that ignore it pick their own. */
  function drawCanvas(registration, width) {
    var ready = Promise.resolve();
    if (registration.fontsReady && document.fonts && document.fonts.load) {
      ready = Promise.all(
        registration.fontsReady.map(function (family) {
          return document.fonts.load('400 100px "' + family + '"');
        })
      ).catch(function () { /* draw anyway with fallback fonts */ });
    }

    return ready.then(function () {
      var canvas = document.createElement('canvas');
      return Promise.resolve(registration.draw(canvas, width)).then(function () {
        if (!canvas.width || !canvas.height) {
          throw new Error('rs-preview-capture: draw produced an empty canvas');
        }
        return canvas;
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Listing picture: 1500x1500 white, sign 1400px, JPEG under 110 KB    */
  /* ------------------------------------------------------------------ */

  /* Box around every pixel that is not fully transparent (like PIL's
     alpha getbbox), so the svg's margin around the sign is dropped. */
  function opaqueBounds(canvas) {
    var w = canvas.width;
    var h = canvas.height;
    var data = canvas.getContext('2d').getImageData(0, 0, w, h).data;
    var minX = w, minY = h, maxX = -1, maxY = -1;
    for (var y = 0; y < h; y++) {
      var row = y * w * 4;
      for (var x = 0; x < w; x++) {
        if (data[row + x * 4 + 3] !== 0) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) throw new Error('rs-preview-capture: empty picture');
    return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
  }

  function canvasToJpegBlob(canvas, quality) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob);
        else reject(new Error('rs-preview-capture: toBlob returned null'));
      }, 'image/jpeg', quality);
    });
  }

  function smallEnoughJpeg(canvas, quality) {
    return canvasToJpegBlob(canvas, quality).then(function (blob) {
      if (blob.size <= LISTING_MAX_BYTES || quality <= 0.4) return blob;
      return smallEnoughJpeg(canvas, Math.round((quality - 0.05) * 100) / 100);
    });
  }

  function listingImage(registration) {
    var render = registration.draw
      ? drawCanvas(registration, LISTING_RENDER)
      : renderSvg(registration, function (w, h) {
          return w >= h ? LISTING_RENDER : LISTING_RENDER * (w / h);
        });

    return render.then(function (sign) {
      var box = opaqueBounds(sign);
      var scale = LISTING_SIGN / Math.max(box.w, box.h);
      var dw = Math.round(box.w * scale);
      var dh = Math.round(box.h * scale);

      var out = document.createElement('canvas');
      out.width = LISTING_CANVAS;
      out.height = LISTING_CANVAS;
      var ctx = out.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, LISTING_CANVAS, LISTING_CANVAS);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(sign, box.x, box.y, box.w, box.h,
        Math.round((LISTING_CANVAS - dw) / 2), Math.round((LISTING_CANVAS - dh) / 2), dw, dh);

      return smallEnoughJpeg(out, LISTING_QUALITY);
    });
  }

  /* ------------------------------------------------------------------ */
  /* "Preview full size" button + the new tab it opens                   */
  /* ------------------------------------------------------------------ */

  var BUTTON_CSS =
    '.rs-pv-row{display:flex;justify-content:center;margin:10px 0 0}' +
    '.rs-pv-row .rs-pv-btn{display:inline-flex;align-items:center;gap:8px;min-height:36px;margin:0;' +
    'padding:0 14px;border:1.5px solid #c9c9c4;border-radius:8px;background:#fff;color:#242424;' +
    'font:inherit;font-size:14px;font-weight:700;line-height:1.2;letter-spacing:normal;' +
    'text-transform:none;cursor:pointer;box-shadow:none}' +
    '.rs-pv-row .rs-pv-btn:hover{background:#f3f3f1}' +
    '.rs-pv-row .rs-pv-btn:focus-visible{outline:2px solid #111;outline-offset:1px}' +
    '.rs-pv-row .rs-pv-btn[aria-busy="true"]{cursor:progress;opacity:.7}' +
    '.rs-pv-row .rs-pv-btn svg{display:block;flex:none}';

  var EYE_ICON =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
    '<path d="M1.5 8S4 3.5 8 3.5 14.5 8 14.5 8 12 12.5 8 12.5 1.5 8 1.5 8Z" stroke="currentColor" ' +
    'stroke-width="1.6" stroke-linejoin="round"/><circle cx="8" cy="8" r="2" stroke="currentColor" ' +
    'stroke-width="1.6"/></svg>';

  var BUTTON_LABEL = 'Preview full size';

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function productHandle() {
    var link = document.querySelector('link[rel="canonical"]');
    var path = (link && link.getAttribute('href')) || location.pathname;
    var match = /\/products\/([^/?#]+)/.exec(path);
    return match ? decodeURIComponent(match[1]) : 'sign-preview';
  }

  function productTitle() {
    var meta = document.querySelector('meta[property="og:title"]');
    return (meta && meta.getAttribute('content')) || document.title.split(' – ')[0];
  }

  function tabPage(body) {
    return '<!doctype html><html lang="en"><head><meta charset="utf-8">' +
      '<meta name="viewport" content="width=device-width,initial-scale=1">' +
      '<meta name="robots" content="noindex"><title>Sign preview</title><style>' +
      'body{margin:0;background:#f5f5f5;color:#242424;font-family:Roboto,Arial,sans-serif}' +
      '.wrap{max-width:760px;margin:0 auto;padding:28px 16px 40px;text-align:center}' +
      'h1{margin:0 0 6px;font-size:22px}' +
      '.meta{margin:0 0 18px;font-size:15px;line-height:1.5;color:#555}' +
      'img{display:block;width:auto;height:auto;max-width:100%;max-height:calc(100vh - 230px);' +
      'min-height:280px;margin:0 auto;background:#fff;border:1px solid #e3e3e0;object-fit:contain}' +
      '.btn{display:inline-block;margin-top:18px;padding:12px 22px;border-radius:8px;background:#111;' +
      'color:#fff;font-size:15px;font-weight:700;text-decoration:none}' +
      '.note{margin:12px 0 0;font-size:14px;color:#555}' +
      '</style></head><body><div class="wrap">' + body + '</div></body></html>';
  }

  function writeTab(win, html) {
    win.document.open();
    win.document.write(html);
    win.document.close();
  }

  function downloadPicture(url, filename) {
    var a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.parentNode.removeChild(a);
  }

  function openPreview(registration, button) {
    if (button.getAttribute('aria-busy') === 'true') return;

    /* Open the tab inside the click so pop-up blockers allow it, then fill
       it in once the picture is ready. */
    var win = null;
    try { win = window.open('', '_blank'); } catch (error) { win = null; }
    if (win) writeTab(win, tabPage('<p class="meta">Making your preview...</p>'));

    var scope = button.closest('.shopify-section') || document;
    var sizeLabel = scope.querySelector('.rs-size-label');
    var size = sizeLabel ? sizeLabel.textContent.trim() : '';
    var filename = productHandle() + '.jpg';

    button.setAttribute('aria-busy', 'true');

    Promise.race([listingImage(registration), timeout(LISTING_TIMEOUT_MS)])
      .then(blobToDataURL)
      .then(function (url) {
        if (!win || win.closed) {
          downloadPicture(url, filename);
          return;
        }
        writeTab(win, tabPage(
          '<h1>Your sign preview</h1>' +
          '<p class="meta">' + escapeHtml(productTitle()) + (size ? '<br>' + escapeHtml(size) : '') + '</p>' +
          '<img src="' + url + '" width="' + LISTING_CANVAS + '" height="' + LISTING_CANVAS + '" alt="Preview of your custom sign">' +
          '<a class="btn" href="' + url + '" download="' + escapeHtml(filename) + '">Download picture</a>' +
          '<p class="note">You can also right-click the picture and choose Save Image As. On a phone, press and hold the picture.</p>'
        ));
      })
      .catch(function (error) {
        if (window.console && console.warn) console.warn('rs-preview-capture: preview failed', error);
        if (win && !win.closed) {
          writeTab(win, tabPage(
            '<h1>Sorry, the preview did not load</h1>' +
            '<p class="meta">Please close this tab and try again. Your design on the product page is not affected.</p>'
          ));
        }
      })
      .then(function () {
        button.removeAttribute('aria-busy');
      });
  }

  function addPreviewButton(registration) {
    var form = document.querySelector(registration.form);
    var scope = (form && form.closest('.shopify-section')) || document;
    var sizeLabel = scope.querySelector('.rs-size-label');
    if (!sizeLabel) return; /* no builder preview on this page */
    var next = sizeLabel.nextElementSibling;
    if (next && next.classList.contains('rs-pv-row')) return;

    if (!document.getElementById('rs-pv-style')) {
      var style = document.createElement('style');
      style.id = 'rs-pv-style';
      style.textContent = BUTTON_CSS;
      document.head.appendChild(style);
    }

    var row = document.createElement('div');
    row.className = 'rs-pv-row';
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'rs-pv-btn';
    button.innerHTML = EYE_ICON + '<span>' + BUTTON_LABEL + '</span>';
    button.setAttribute('aria-label', BUTTON_LABEL + ' (opens in a new tab)');
    button.addEventListener('click', function () {
      openPreview(registration, button);
    });
    row.appendChild(button);
    sizeLabel.parentNode.insertBefore(row, sizeLabel.nextSibling);
  }

  /* ------------------------------------------------------------------ */
  /* Form plumbing                                                       */
  /* ------------------------------------------------------------------ */

  function removePreviewInput(form) {
    Array.prototype.forEach.call(
      form.querySelectorAll('input[data-rs-preview-input]'),
      function (input) { input.parentNode.removeChild(input); }
    );
  }

  function attachPreviewFile(form, blob) {
    /* DataTransfer is the only way to populate a file input; old Safari
       throws on the constructor — treated as a soft failure. */
    var dataTransfer = new DataTransfer();
    dataTransfer.items.add(new File([blob], 'sign-preview.png', { type: 'image/png' }));

    var input = document.createElement('input');
    input.type = 'file';
    input.name = PROPERTY_NAME;
    input.hidden = true;
    input.tabIndex = -1;
    input.setAttribute('aria-hidden', 'true');
    input.setAttribute('data-rs-preview-input', '');
    input.files = dataTransfer.files;
    form.appendChild(input);
  }

  function submitForm(form) {
    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      /* Dispatched events do not trigger native submission, but Dawn's
         product-form.js submit handler runs and takes over via fetch. */
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  }

  function attach(registration) {
    var form = document.querySelector(registration.form);
    var button = registration.button ? document.querySelector(registration.button) : null;
    if (!form || !button) return; /* theme editor / partial render: no-op */
    if (button.hasAttribute('data-rs-preview-bound')) return;
    button.setAttribute('data-rs-preview-bound', '');

    var capturing = false;

    button.addEventListener('click', function (event) {
      if (capturing) {
        event.preventDefault();
        return;
      }
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      /* Invalid form: let the native validation UI fire, nothing submits. */
      if (typeof form.checkValidity === 'function' && !form.checkValidity()) return;

      event.preventDefault();
      capturing = true;

      var capture = registration.draw ? captureCanvas(registration) : captureSvg(registration);

      Promise.race([capture, timeout(CAPTURE_TIMEOUT_MS)])
        .then(function (blob) {
          removePreviewInput(form); /* drop a stale preview from a prior add */
          attachPreviewFile(form, blob);
        })
        .catch(function (error) {
          removePreviewInput(form);
          if (window.console && console.warn) {
            console.warn('rs-preview-capture: continuing without preview', error);
          }
        })
        .then(function () {
          capturing = false;
          submitForm(form);
        });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Queue drain                                                         */
  /* ------------------------------------------------------------------ */

  var queue = window.RSPreviewQueue = window.RSPreviewQueue || [];
  var pending = queue.slice();
  queue.length = 0;
  queue.push = function (registration) {
    try {
      attach(registration);
    } catch (error) {
      if (window.console && console.warn) {
        console.warn('rs-preview-capture: registration failed', error);
      }
    }
    try {
      addPreviewButton(registration);
    } catch (error) {
      if (window.console && console.warn) {
        console.warn('rs-preview-capture: preview button failed', error);
      }
    }
    return 0;
  };
  pending.forEach(queue.push);
})();